import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, Circle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ConclaveEventShell } from '@/components/conclave/ConclaveEventShell';
import { DetailEntriesEditor } from '@/components/conclave/DetailEntriesEditor';
import { CategoryMultiPicker } from '@/components/conclave/CategoryMultiPicker';
import { EventGivesAsksEditor } from '@/components/conclave/EventGivesAsksEditor';
import { conclaveApi, type EventGiveAskItem } from '@/lib/conclave-api';
import { conclaveSubPath } from '@/lib/conclave-paths';
import {
  ensureDetailEntries,
  hasAnyNetworkingDetail,
  parseCategoryOptions,
  parseDreamReferralList,
  type BusinessCategoryOption,
  type NetworkingDetail,
} from '@/lib/conclave-networking';
import { toast } from 'sonner';

const MIN_GIVES = 3;
const MIN_ASKS = 3;
const MIN_DREAM = 1;
const MIN_CONNECTS = 3;

type ChecklistItem = {
  key: string;
  label: string;
  anchor: string;
  current: number;
  target: number;
};

function SectionCard({
  anchor,
  step,
  title,
  help,
  optional,
  status,
  children,
}: {
  anchor: string;
  step: number;
  title: string;
  help: string;
  optional?: boolean;
  status?: { current: number; target: number };
  children: ReactNode;
}) {
  const done = status ? status.current >= status.target : false;
  return (
    <section
      id={anchor}
      className="scroll-mt-24 space-y-4 rounded-2xl border border-border bg-card p-4 shadow-sm md:p-5"
    >
      <div className="flex items-start gap-3">
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
            done ? 'bg-emerald-500 text-white' : 'bg-primary/10 text-primary'
          }`}
        >
          {done ? <CheckCircle2 className="h-4 w-4" /> : step}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-foreground">{title}</h2>
            {optional ? (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                Optional
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{help}</p>
        </div>
        {status ? (
          <span
            className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${
              done ? 'bg-emerald-500/10 text-emerald-700' : 'bg-amber-500/10 text-amber-700'
            }`}
          >
            {Math.min(status.current, status.target)} of {status.target}
          </span>
        ) : null}
      </div>
      {children}
    </section>
  );
}

export default function ConclaveNetworkingProfilePage() {
  const { eventId = '', communityId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnToLive = (location.state as { returnToLive?: boolean } | null)?.returnToLive === true;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [giveAsks, setGiveAsks] = useState<EventGiveAskItem[]>([]);
  const [dreamReferral, setDreamReferral] = useState<NetworkingDetail[]>([
    ensureDetailEntries(null)[0],
  ]);
  const [connectCategories, setConnectCategories] = useState<BusinessCategoryOption[]>([]);
  const [giveBusinessCategories, setGiveBusinessCategories] = useState<BusinessCategoryOption[]>(
    []
  );
  const [city, setCity] = useState('');
  const [intros, setIntros] = useState<Array<{ id: string; title: string; body: string }>>([]);
  const [activeIntroId, setActiveIntroId] = useState<string | null>(null);
  const [introTitle, setIntroTitle] = useState('');
  const [introBody, setIntroBody] = useState('');
  const [seatPrefEnabled, setSeatPrefEnabled] = useState(false);
  const [seatPrefIds, setSeatPrefIds] = useState('');

  const reloadGiveAsks = async () => {
    const res = await conclaveApi.listEventGiveAsks(eventId);
    setGiveAsks(res.items);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [profileRes, configRes, giveAskRes] = await Promise.all([
          conclaveApi.getProfile(eventId),
          conclaveApi.getConfig(eventId),
          conclaveApi.listEventGiveAsks(eventId),
        ]);
        if (cancelled) return;
        setGiveAsks(giveAskRes.items);
        const profile = profileRes.profile as Record<string, unknown> | null;
        if (profile) {
          setDreamReferral(ensureDetailEntries(parseDreamReferralList(profile.dreamReferral)));
          setConnectCategories(parseCategoryOptions(profile.connectCategories));
          setGiveBusinessCategories(parseCategoryOptions(profile.giveBusinessCategories));
          setCity(String(profile.city || ''));
          setActiveIntroId((profile.activeIntroId as string) || null);
        }
        setIntros((profileRes.intros as typeof intros) || []);
        setSeatPrefEnabled(Boolean(configRes.config?.seatPreferenceEnabled));
        if (configRes.config?.seatPreferenceEnabled) {
          const prefs = await conclaveApi.getSeatPrefs(eventId);
          setSeatPrefIds(
            ((prefs.preferences as Array<{ toRegistrationId: string }>) || [])
              .map((p) => p.toRegistrationId)
              .join(', ')
          );
        }
      } catch {
        toast.error('Failed to load profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const enabledGives = giveAsks.filter((item) => item.type === 'GIVE' && item.enabled).length;
  const enabledAsks = giveAsks.filter((item) => item.type === 'ASK' && item.enabled).length;
  const dreamFilled = dreamReferral.filter(hasAnyNetworkingDetail).length;

  const checklist: ChecklistItem[] = [
    {
      key: 'gives',
      label: 'Gives',
      anchor: 'profile-gives',
      current: enabledGives,
      target: MIN_GIVES,
    },
    { key: 'asks', label: 'Asks', anchor: 'profile-asks', current: enabledAsks, target: MIN_ASKS },
    {
      key: 'dream',
      label: 'Dream referral',
      anchor: 'profile-dream',
      current: dreamFilled,
      target: MIN_DREAM,
    },
    {
      key: 'seek',
      label: 'Seek connects',
      anchor: 'profile-seek',
      current: connectCategories.length,
      target: MIN_CONNECTS,
    },
    {
      key: 'your',
      label: 'Your connects',
      anchor: 'profile-your',
      current: giveBusinessCategories.length,
      target: MIN_CONNECTS,
    },
  ];
  const doneCount = checklist.filter((item) => item.current >= item.target).length;
  const missing = checklist
    .filter((item) => item.current < item.target)
    .map((item) => {
      const remaining = item.target - item.current;
      return item.key === 'dream'
        ? `${remaining} dream referral`
        : `${remaining} more ${item.label.toLowerCase()}`;
    });
  const missingText = missing.join(', ');

  async function saveProfile(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await conclaveApi.putProfile(eventId, {
        dreamReferral: dreamReferral.filter(hasAnyNetworkingDetail).slice(0, MIN_DREAM),
        connectCategories,
        giveBusinessCategories,
        activeIntroId,
        city: city.trim() || null,
      });
      if (seatPrefEnabled) {
        await conclaveApi.putSeatPrefs(
          eventId,
          seatPrefIds
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        );
      }
      if (missing.length === 0) {
        toast.success('Profile saved. You are ready for seating.');
      } else {
        toast.success(`Saved. Still to add: ${missingText}`);
      }
      if (returnToLive) {
        navigate(conclaveSubPath(eventId, 'live', communityId), { replace: true });
      }
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function addIntro() {
    const title = introTitle.trim();
    const body = introBody.trim();
    if (!title || !body) return;
    try {
      const res = await conclaveApi.createIntro(eventId, { title, body });
      setIntros((prev) => [res.intro, ...prev]);
      setActiveIntroId(res.intro.id);
      setIntroTitle('');
      setIntroBody('');
      toast.success('Intro added');
    } catch {
      toast.error('Failed to add intro');
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <ConclaveEventShell title="Networking profile">
      <p className="mt-1 text-sm text-muted-foreground">
        The organiser uses this to seat you with the right people. Fill in the required steps, save
        whenever you like, and come back to finish later.
      </p>

      <div className="mt-4 rounded-2xl border border-border bg-card p-4 md:mt-6">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-foreground">Checklist</p>
          <p className="text-xs text-muted-foreground">
            {doneCount} of {checklist.length} done
          </p>
        </div>
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-all"
            style={{ width: `${Math.round((doneCount / checklist.length) * 100)}%` }}
          />
        </div>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {checklist.map((item) => {
            const done = item.current >= item.target;
            return (
              <li key={item.key}>
                <a
                  href={`#${item.anchor}`}
                  className="flex items-center justify-between gap-2 rounded-xl border border-border bg-background px-3 py-2 text-sm transition hover:border-primary/40"
                >
                  <span className="flex min-w-0 items-center gap-2 text-foreground">
                    {done ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                    ) : (
                      <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <span className="truncate">{item.label}</span>
                  </span>
                  <span
                    className={`shrink-0 text-xs font-medium ${
                      done ? 'text-emerald-700' : 'text-muted-foreground'
                    }`}
                  >
                    {Math.min(item.current, item.target)}/{item.target}
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      </div>

      <form onSubmit={saveProfile} className="mt-4 space-y-4 md:mt-6 md:space-y-6">
        <SectionCard
          anchor="profile-gives"
          step={1}
          title="Your gives"
          help="People or companies you are closely connected to and can refer business to. Don't add yourself. Add at least 3 and keep them switched on. Enabled items are shown to your table; gives and asks save as you change them."
          status={{ current: enabledGives, target: MIN_GIVES }}
        >
          <EventGivesAsksEditor
            eventId={eventId}
            kind="GIVE"
            items={giveAsks}
            onChanged={reloadGiveAsks}
          />
        </SectionCard>

        <SectionCard
          anchor="profile-asks"
          step={2}
          title="Your asks"
          help="What you are looking for right now, such as a service, a supplier or a lead. Add at least 3 so your table knows how to help you."
          status={{ current: enabledAsks, target: MIN_ASKS }}
        >
          <EventGivesAsksEditor
            eventId={eventId}
            kind="ASK"
            items={giveAsks}
            onChanged={reloadGiveAsks}
          />
        </SectionCard>

        <SectionCard
          anchor="profile-dream"
          step={3}
          title="Dream referral"
          help="One introduction that would change your business. Describe the person or company you would most like to be introduced to."
          status={{ current: dreamFilled, target: MIN_DREAM }}
        >
          <DetailEntriesEditor
            values={dreamReferral}
            onChange={(next) => setDreamReferral(next.slice(0, 1))}
            itemLabel="Dream"
            tip="Describe a dream introduction that would change everything for your business."
            maxItems={1}
          />
        </SectionCard>

        <SectionCard
          anchor="profile-seek"
          step={4}
          title="Seek connects"
          help="Business categories you want to get business from. Pick at least 3."
          status={{ current: connectCategories.length, target: MIN_CONNECTS }}
        >
          <CategoryMultiPicker values={connectCategories} onChange={setConnectCategories} />
        </SectionCard>

        <SectionCard
          anchor="profile-your"
          step={5}
          title="Your connects"
          help="Business categories you can offer business in. Pick at least 3."
          status={{ current: giveBusinessCategories.length, target: MIN_CONNECTS }}
        >
          <CategoryMultiPicker
            values={giveBusinessCategories}
            onChange={setGiveBusinessCategories}
          />
        </SectionCard>

        <SectionCard
          anchor="profile-intros"
          step={6}
          title="Speaking intros"
          optional
          help="Short pitches for live table rounds. Select one as active; it shows when you speak."
        >
          {intros.length > 0 ? (
            <ul className="space-y-2">
              {intros.map((intro) => (
                <li
                  key={intro.id}
                  className="rounded-xl border border-border bg-background px-3 py-3 text-sm"
                >
                  <label className="flex items-start gap-2">
                    <input
                      type="radio"
                      name="activeIntro"
                      checked={activeIntroId === intro.id}
                      onChange={() => setActiveIntroId(intro.id)}
                      className="mt-1"
                    />
                    <span className="min-w-0">
                      <strong className="block text-foreground">{intro.title}</strong>
                      <span className="mt-0.5 block text-muted-foreground">{intro.body}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          ) : (
            <p className="rounded-xl border border-dashed border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              No intros yet. Add one below if you want to speak in live rounds.
            </p>
          )}

          <div className="space-y-3 border-t border-border pt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Add an intro
            </p>
            <Input
              placeholder="Title, e.g. Export compliance"
              value={introTitle}
              onChange={(e) => setIntroTitle(e.target.value)}
            />
            <Textarea
              placeholder="One or two lines about what you do and who you help"
              value={introBody}
              onChange={(e) => setIntroBody(e.target.value)}
              className="min-h-24"
            />
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                disabled={!introTitle.trim() || !introBody.trim()}
                onClick={() => {
                  void addIntro();
                }}
              >
                Create intro
              </Button>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          anchor="profile-city"
          step={7}
          title="City"
          optional
          help="Where you are based. The organiser uses it to seat people from the same city together where possible."
        >
          <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Pune" />
        </SectionCard>

        {seatPrefEnabled ? (
          <SectionCard
            anchor="profile-seat"
            step={8}
            title="Seating preference"
            optional
            help="Registration IDs of people you would like to sit with, separated by commas. We try to honour this but cannot guarantee it."
          >
            <Input value={seatPrefIds} onChange={(e) => setSeatPrefIds(e.target.value)} />
          </SectionCard>
        ) : null}

        <div className="sticky bottom-0 z-10 -mx-4 flex items-center justify-between gap-3 border-t border-border bg-background/95 px-4 py-3 backdrop-blur md:mx-0 md:rounded-2xl md:border md:px-4">
          <p className="min-w-0 text-xs text-muted-foreground">
            {missing.length === 0
              ? 'Everything required is in place.'
              : `Still to add: ${missingText}`}
          </p>
          <Button type="submit" className="shrink-0 rounded-xl" disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              'Save profile'
            )}
          </Button>
        </div>
      </form>
    </ConclaveEventShell>
  );
}
